import { Language } from "@/i18n/translations";
import { supabase } from "./supabase";

const STORAGE_KEY = "vd-cms";

export type Testimonial = {
  name: string;
  location: string;
  text: string;
  rating: number;
};

export type PricingPlan = {
  nameKey: string;
  price: string;
  descKey: string;
  featureKeys: string[];
  featured: boolean;
};

export type CMSImages = {
  hero?: string;
  logo?: string;
  story?: string;
  og?: string;
  service_1?: string;
  service_2?: string;
  service_3?: string;
  service_4?: string;
  service_5?: string;
  service_6?: string;
  [key: string]: string | undefined;
};

export type CMSData = {
  overrides: Partial<Record<Language, Record<string, string>>>;
  testimonials: Testimonial[];
  pricing: PricingPlan[];
  images: CMSImages;
  contact: {
    email: string;
    phone: string;
    hours: string;
  };
};

const DEFAULT_TESTIMONIALS: Testimonial[] = [
  {
    name: "Marija S.",
    location: "Vienna → Belgrade",
    text: "I was so worried about my mother living alone. Now I get daily photos — I can see she's eating well, the apartment is clean, and she's smiling again.",
    rating: 5,
  },
  {
    name: "Andrei P.",
    location: "Munich → Bucharest",
    text: "My father refused to move to a retirement home. Now he has Elena who visits every day, cooks his favorite dishes, and takes him for walks. It's like having family nearby.",
    rating: 5,
  },
  {
    name: "Iván K.",
    location: "London → Budapest",
    text: "The pharmacy trips alone have been a lifesaver. My parents no longer need to navigate crowded clinics. The caregiver even joins their video calls with us.",
    rating: 5,
  },
];

const DEFAULT_PRICING: PricingPlan[] = [
  {
    nameKey: "pricing_essential",
    price: "€99",
    descKey: "pricing_essential_desc",
    featureKeys: [
      "pricing_feature_2visits",
      "pricing_feature_errands",
      "pricing_feature_housekeeping",
      "pricing_feature_weeklyPhotos",
      "pricing_feature_phoneSupport",
    ],
    featured: false,
  },
  {
    nameKey: "pricing_comfort",
    price: "€199",
    descKey: "pricing_comfort_desc",
    featureKeys: [
      "pricing_feature_5visits",
      "pricing_feature_allEssential",
      "pricing_feature_meals",
      "pricing_feature_pharmacy",
      "pricing_feature_dailyPhotos",
      "pricing_feature_dedicatedCaregiver",
    ],
    featured: true,
  },
  {
    nameKey: "pricing_premium",
    price: "€349",
    descKey: "pricing_premium_desc",
    featureKeys: [
      "pricing_feature_dailyVisits",
      "pricing_feature_allComfort",
      "pricing_feature_walks",
      "pricing_feature_doctorVisit",
      "pricing_feature_realtime",
      "pricing_feature_emergency",
      "pricing_feature_videoReport",
    ],
    featured: false,
  },
];

function getDefault(): CMSData {
  return {
    overrides: {},
    testimonials: DEFAULT_TESTIMONIALS,
    pricing: DEFAULT_PRICING,
    images: {},
    contact: {
      email: "office@vita-dom.org",
      phone: "+381 11 123 4567",
      hours: "Mon-Fri 9:00-18:00 CET",
    },
  };
}

export function loadCMS(): CMSData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefault();
    const parsed = JSON.parse(raw) as Partial<CMSData>;
    return { ...getDefault(), ...parsed };
  } catch {
    return getDefault();
  }
}

export function saveCMS(data: CMSData) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function exportCMS(data: CMSData): string {
  return JSON.stringify(data, null, 2);
}

export function importCMS(json: string): CMSData {
  const parsed = JSON.parse(json) as Partial<CMSData>;
  return { ...getDefault(), ...parsed };
}

export function imageToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function loadCMSFromSupabase(): Promise<Partial<CMSData>> {
  try {
    const [settingsRes, imagesRes] = await Promise.all([
      supabase.from("cms_settings").select("key, value"),
      supabase.from("cms_images").select("slot, url"),
    ]);

    const result: Partial<CMSData> = {};

    if (settingsRes.data) {
      for (const row of settingsRes.data) {
        if (row.key === "contact" && row.value) {
          result.contact = row.value as CMSData["contact"];
        }
        if (row.key === "colors" && row.value) {
          applyColorOverrides(row.value as Record<string, string>);
        }
      }
    }

    if (imagesRes.data && imagesRes.data.length > 0) {
      const images: CMSImages = {};
      for (const row of imagesRes.data) {
        images[row.slot] = row.url;
      }
      result.images = images;
    }

    return result;
  } catch {
    return {};
  }
}

function applyColorOverrides(colors: Record<string, string>) {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(colors)) {
    if (value) root.style.setProperty(key, value);
  }
}
